import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { X, Upload, Image as ImageIcon, Camera } from 'lucide-react';
import { toast } from 'sonner';

export interface LayoutPhotoFile {
  file: File;
  preview: string;
  caption?: string;
  id: string;
}

interface LayoutPhotoUploadProps {
  photos: LayoutPhotoFile[] | undefined;
  onPhotosChange: (photos: LayoutPhotoFile[]) => void;
  maxPhotos?: number;
  disabled?: boolean;
}

const LayoutPhotoUpload: React.FC<LayoutPhotoUploadProps> = ({
  photos,
  onPhotosChange,
  maxPhotos = 3,
  disabled = false,
}) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // Defensive programming: ensure photos is never undefined
  const safePhotos = Array.isArray(photos) ? photos : [];

  // Additional safety check to prevent any edge cases
  React.useEffect(() => {
    if (photos !== undefined && !Array.isArray(photos)) {
      console.warn('LayoutPhotoUpload: photos prop is not an array:', photos);
    }
    if (typeof onPhotosChange !== 'function') {
      console.error('LayoutPhotoUpload: onPhotosChange prop is not a function or is missing:', onPhotosChange);
    }
  }, [photos, onPhotosChange]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (disabled) return;

      const remainingSlots = maxPhotos - safePhotos.length;
      if (remainingSlots === 0) {
        toast.error(`Maximum ${maxPhotos} photos allowed`);
        return;
      }

      const filesToAdd = acceptedFiles.slice(0, remainingSlots);
      const newPhotos: LayoutPhotoFile[] = filesToAdd.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
        caption: '',
        id: `${Date.now()}-${Math.random()}`,
      }));

      if (filesToAdd.length < acceptedFiles.length) {
        toast.warning(`Only added ${filesToAdd.length} photos (maximum ${maxPhotos} allowed)`);
      }

      if (typeof onPhotosChange === 'function') {
        onPhotosChange([...safePhotos, ...newPhotos]);
      } else {
        console.error('LayoutPhotoUpload: Cannot call onPhotosChange - it is not a function');
      }
    },
    [safePhotos, onPhotosChange, maxPhotos, disabled]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif'],
    },
    maxFiles: maxPhotos,
    maxSize: 5 * 1024 * 1024, // 5MB
    disabled,
    onDropRejected: (rejectedFiles) => {
      rejectedFiles.forEach((rejection) => {
        const errors = rejection.errors.map((error) => {
          switch (error.code) {
            case 'file-too-large':
              return 'File is too large (max 5MB)';
            case 'file-invalid-type':
              return 'Invalid file type (only JPG, PNG, GIF allowed)';
            default:
              return error.message;
          }
        });
        toast.error(errors.join(', '));
      });
    },
  });

  const removePhoto = (index: number) => {
    if (disabled) return;

    const newPhotos = [...safePhotos];
    // Revoke the object URL to prevent memory leaks
    URL.revokeObjectURL(newPhotos[index].preview);
    newPhotos.splice(index, 1);
    if (typeof onPhotosChange === 'function') {
      onPhotosChange(newPhotos);
    }
  };

  const updateCaption = (index: number, caption: string) => {
    if (disabled) return;

    const newPhotos = [...safePhotos];
    newPhotos[index].caption = caption;
    if (typeof onPhotosChange === 'function') {
      onPhotosChange(newPhotos);
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (disabled || draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const newPhotos = [...safePhotos];
    const draggedPhoto = newPhotos[draggedIndex];

    // Remove the dragged photo from its original position
    newPhotos.splice(draggedIndex, 1);

    // Insert it at the new position
    const adjustedDropIndex = draggedIndex < dropIndex ? dropIndex - 1 : dropIndex;
    newPhotos.splice(adjustedDropIndex, 0, draggedPhoto);

    if (typeof onPhotosChange === 'function') {
      onPhotosChange(newPhotos);
    }
    setDraggedIndex(null);
  };

  const canAddMore = safePhotos.length < maxPhotos;

  // Ultimate safety guard - if something is fundamentally wrong, render gracefully
  if (typeof maxPhotos !== 'number' || maxPhotos < 0) {
    console.error('LayoutPhotoUpload: invalid maxPhotos prop:', maxPhotos);
    return <div className="text-red-500">Photo upload configuration error</div>;
  }

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      {canAddMore && !disabled && (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            isDragActive
              ? 'border-primary bg-primary/10'
              : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center gap-2">
            <Camera className="h-8 w-8 text-gray-400" />
            <p className="text-sm text-gray-600">
              {isDragActive
                ? 'Drop photos here...'
                : 'Drag & drop layout photos here, or click to select'}
            </p>
            <p className="text-xs text-gray-500">
              Up to {maxPhotos} photos • JPG, PNG, GIF • Max 5MB each
            </p>
            <Button type="button" variant="outline" size="sm" className="mt-2">
              <Upload className="h-4 w-4 mr-2" />
              Choose Photos
            </Button>
          </div>
        </div>
      )}

      {/* Photo Grid */}
      {safePhotos.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {safePhotos.map((photo, index) => (
            <Card
              key={photo.id}
              className={`relative group ${
                draggedIndex === index ? 'opacity-50' : ''
              }`}
              draggable={!disabled}
              onDragStart={() => handleDragStart(index)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
            >
              <CardContent className="p-3">
                <div className="relative aspect-video bg-gray-100 rounded-md overflow-hidden mb-3">
                  <img
                    src={photo.preview}
                    alt={`Layout photo ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  {!disabled && (
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => removePhoto(index)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                  <Badge
                    variant="secondary"
                    className="absolute bottom-2 left-2 text-xs"
                  >
                    {index + 1}
                  </Badge>
                </div>

                <Input
                  placeholder="Add caption (optional)"
                  value={photo.caption || ''}
                  onChange={(e) => updateCaption(index, e.target.value)}
                  disabled={disabled}
                  className="text-sm"
                />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Status */}
      <div className="flex items-center justify-between text-sm text-gray-500">
        <div className="flex items-center gap-1">
          <ImageIcon className="h-4 w-4" />
          <span>{safePhotos.length} of {maxPhotos} photos</span>
        </div>
        {safePhotos.length > 0 && !disabled && (
          <p>Drag photos to reorder</p>
        )}
      </div>
    </div>
  );
};

export default LayoutPhotoUpload;